/** @jsxRuntime classic */
/** @jsx jsx */
import React, { PureComponent, RefObject } from 'react';
import { connect } from 'react-redux';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { TouchTransition, MouseTransition } from 'react-dnd-multi-backend';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import * as Immutable from 'immutable';
import { messages, cannotGoBackDialog } from 'config/messages';

import { RootState } from 'reducers/rootReducer';
import { printConfig } from 'config/printConfig';
import { Wall, CircleWall } from 'types/wall';
import { CoordinatePoint } from 'types/point';
import { Page } from 'types/page';
import { Direction } from 'types/direction';

import { Keys, arrowDeltas } from 'helpers/keys';
import { isIE11 } from 'helpers/browserDetect';
import { coordinatesByAngle } from 'helpers/geometry';
import { formatSketchObjects } from 'helpers/save/formatSketchObjects';
import { svgAsPngUri } from 'helpers/save/svgToPng';
import { parseString } from 'helpers/draw/parseDrawInput';
import { feetToPixels, metersToPixels } from 'helpers/pixelsToDistance';

import { actions as networkPersistenceActions } from 'ducks/persistence/networkPersistence';
import { getUserIsValid, isMobileApplication, getSketchPadMode, login, logout, beforeLogout } from 'effects/auth';
import { selectors as pagesSelectors, } from 'ducks/model/pages';
import { actions as numpadModalActions, selectors as numpadModalSelectors } from 'ducks/modal/numpadModal';
import { selectors as areaTypeModalSelectors } from 'ducks/modal/areaTypeModal';
import { selectors as subAreaModalSelectors } from 'ducks/modal/subAreaModal';
import { selectors as glaBreakdownModalSelectors } from 'ducks/modal/glaBreakdownModal';
import { selectors as pageGroupsModalSelectors } from 'ducks/modal/pageGroupsModal';
import { selectors as printPreviewModalSelectors } from 'ducks/modal/printPreviewModal';
import { selectors as resizeSketchModalSelectors } from 'ducks/modal/resizeSketchModal';
import { selectors as saveSketchModalSelectors } from 'ducks/modal/saveSketchModal';
import { selectors as sketchSearchModalSelectors } from 'ducks/modal/sketchSearchModal';
import { selectors as settingsModalSelectors } from 'ducks/modal/settingsModal';
import { selectors as settingsSelectors } from 'ducks/settings';
import { selectors as selectionSelectors } from 'ducks/selection/selection';
import { selectors as wallSelectors } from 'ducks/model/walls';
import { actions as sketchPersistenceActions, selectors as sketchPersistenceSelectors } from 'ducks/persistence/sketchPersistence';
import { actions as sidebarActions, selectors as sidebarSelectors } from 'ducks/sidebar/sidebar';
import { actions as editModeActions, selectors as editModeSelectors } from 'ducks/editMode';
import { actions as viewportActions, selectors as viewportSelectors, ZoomByIncrementPayload } from 'ducks/viewport';
import { selectors as drawsSelectors, actions as drawActions } from 'ducks/draw/draw';
import { actions as drawCurveActions } from 'ducks/draw/drawCurve';
import { selectors as drawWithKeypadSelectors, actions as drawWithKeypadActions } from 'ducks/draw/drawWithKeypad';
import { actions as moveObjectsActions } from 'ducks/moveObjects';
import { actions as removeObjects } from 'ducks/remove/removeObjects';
import { actions as historyActions, selectors as historySelectors } from 'ducks/history/history';
import { actions as messageContainerActions, onSubmitEventType } from 'ducks/messageContainer';
import { actions as inspectionActions } from 'ducks/inspection';
import { actions as copyActions } from 'ducks/copy/copy';
import { actions as areaTypePersistenceActions } from 'ducks/persistence/areaTypePersistence';
import { selectors as sketchesPersistenceSelectors, actions as sketchesPersistenceActions } from 'ducks/persistence/sketchesPersistence';
import { selectors as unsavedModalSelectors } from 'ducks/modal/unsavedModal';
import { actions as sketchActions } from 'ducks/model/sketch';
import { getDisplayPose } from 'components/animations';
import SketchArea from 'components/sketch/SketchArea/SketchArea';
import AppVersion from 'components/appVersion/AppVersion';
import ToolbarLeft from 'components/toolbars/ToolbarLeft';
import LabelSidebar from 'components/sidebar/LabelSidebar';
import SymbolSidebar from 'components/sidebar/SymbolSidebar';
import PolygonSidebar from 'components/sidebar/PolygonSidebar';
import ToolbarTop from 'components/toolbars/ToolbarTop';
import AreaTypeModal from 'components/modal/AreaTypeModal';
import SubAreaModal from 'components/modal/SubAreaModal';
import GlaBreakdownModal from 'components/modal/GlaBreakdownModal';
import PageGroupsModal from 'components/modal/PageGroupsModal';
import PrintPreviewModal from 'components/modal/PrintPreviewModal';
import ResizeSketchModal from 'components/modal/ResizeSketchModal';
import RescaleGridModal from 'components/modal/RescaleGridModal';
import KnownLengthPromptModal from 'components/modal/KnownLengthPromptModal';
import SettingsModal from 'components/modal/SettingsModal';
import SketchSearchModal from 'components/modal/SketchSearchModal';
import NumpadMenuModal from 'components/modal/NumpadMenuModal';
import PrintTableView from 'components/sketch/PrintTable/PrintTableView';
import MessageContainer from 'components/message/MessageContainer';
import Preview from 'components/elements/DnDPreview';
import UnsavedModal from 'components/modal/UnsavedModal';
import { ThemeProvider } from '@mui/styles';
import AciCoreTheme from 'figma/themes/acitheme';
import SaveSketchModal from 'components/modal/SaveSketchModal';
import { getCompanies } from 'effects/api';
import CompanyList from 'components/CompanyList';
import { ReactComponent as MobileSketchIcon } from 'assets/icons/mobile-sketch.svg';
import { ReactComponent as MobileOrdersIcon } from 'assets/icons/mobile-orders.svg';
import { selectors as orderDetailsSelectors, actions as orderActions } from 'ducks/orderDetails/orderDetails';
import { ReactComponent as ChevronLeft } from 'assets/icons/ChevronLeft.svg';
import { ReactComponent as ChevronRight } from 'assets/icons/ChevronRight.svg';
import { Button } from '@mui/material';
import { ApiSketch } from 'types/api/apiSketch';

type ShortcutDetails = {
  name: string
  keySequence: string
  description: string
  isValidMode?: (event: React.KeyboardEvent) => boolean;
  action: (event: React.KeyboardEvent) => void;
};

// https://github.com/LouisBrunner/dnd-multi-backend/blob/master/packages/react-dnd-multi-backend/src/HTML5toTouch.js
export const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const styles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'none'
});

// flex is so the sketch has a dynamic fit on the page.
// min-height and width allow it to scale down when resizing to change aspect ratio.
const Main = styled.main`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  min-height: 0;
  min-width: 0;
`;

const mobileTabDivStyle = css({
  position: 'absolute',
  height: '70px',
  bottom: 0,
  width: '100%',
  background: 'white',
  zIndex: 2,
  textAlign: 'center',
});

const mobileTabBtnDivStyle = css({
  display: 'inline-block',
  width: '50%',
});

const mobileIconStyle = css({
  verticalAlign: 'middle',
  margin: '0 0 2px 0',
});

const mobileTabSpanStyle = css({
  lineHeight: '70px',
  fontSize: '13px',
  margin: '0 8px',
});


interface StateProps {
  readonly unitOfMeasure: string;
  readonly currentGridSize: number;
  readonly endPoint: CoordinatePoint;
  readonly previousStartPoint: CoordinatePoint;
  readonly viewCenterPoint: CoordinatePoint;
  readonly isDrawing: boolean;
  readonly isPanning: boolean;
  readonly isSelectedMode: boolean;
  readonly isShowingNumpadModal: boolean;
  readonly isShowingAreaTypeModal: boolean;
  readonly isShowingSubAreaModal: boolean;
  readonly isShowingGlaBreakdownModal: boolean;
  readonly isShowingPageGroupsModal: boolean;
  readonly isShowingPrintPreviewModal: boolean;
  readonly isShowingSaveSketchModal: boolean;
  readonly isShowingResizeSketchModal: boolean;
  readonly isShowingRescaleGridModal: boolean;
  readonly isShowingKnownLengthPromptModal: boolean;
  readonly isShowingSketchSearchModal: boolean;
  readonly isShowingSettingsModal: boolean;
  readonly isShowingLabelSidebar: boolean;
  readonly isShowingToolbarLeft: boolean;
  readonly isShowingSymbolSidebar: boolean;
  readonly isShowingPolygonSidebar: boolean;
  readonly isInStampMode: boolean;
  readonly canCreateCurve: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly selectedObjects: Immutable.List<string>;
  readonly currentPage: Page | undefined;
  readonly svgElement: SVGSVGElement | null;
  readonly drawInputSequence: string[];
  readonly currentValue: string;
  readonly currentDirection: Direction | string | undefined;
  readonly currentAngleValue: string;
  readonly walls: Immutable.Map<string, Wall | CircleWall>;
  readonly isAngleInputEnabled: boolean;
  readonly isApplyingModel: boolean;
  readonly userCompany: string;
  readonly sketches: ApiSketch[];
  readonly sketchesAreLoading: boolean;
  readonly isShowingUnsavedModal: boolean;
}

interface ActionProps {
  readonly setEndPoint: (endPoint: CoordinatePoint, center: boolean) => void;
  readonly loadAreaTypes: typeof areaTypePersistenceActions.loadAreaTypes;
  readonly toggleDrawingStraightWalls: (value: boolean) => void;
  readonly addCurve: () => void;
  readonly zoomViewBox: (zoomByIncrementPayload: ZoomByIncrementPayload) => void;
  readonly startPan: (point: CoordinatePoint) => void;
  readonly panViewBox: (point: CoordinatePoint) => void;
  readonly endPan: () => void;
  readonly resetViewBox: () => void;
  readonly centerAll: () => void;
  readonly removeLastInput: () => void;
  readonly addDrawInput: (inputKey: string) => void;
  readonly finishDrawInput: () => void;
  readonly jumpMoveByKeypad: (key: string) => void;
  readonly moveByKeypad: (key: string) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly removeSelectedObjects: () => void;
  readonly showLabelSidebar: () => void;
  readonly showSymbolSidebar: () => void;
  readonly hideSidebar: () => void;
  readonly hidePolygonSidebar: () => void;
  readonly showPolygonSidebar: () => void;
  readonly toggleStampMode: () => void;
  readonly autoFinishSegment: () => void;
  readonly toggleDrawingInteriorWalls: (drawInteriorWalls: boolean) => void;
  readonly setCurrentDirection: typeof drawWithKeypadActions.setCurrentDirection;
  readonly setAngleInput: typeof drawWithKeypadActions.setAngleInput;
  readonly resetDrawInput: typeof drawWithKeypadActions.resetDrawInput;
  readonly endDraw: typeof editModeActions.switchToSelection;
  readonly switchToSelection: typeof editModeActions.switchToSelection;
  readonly switchToEdit: typeof editModeActions.switchToEdit;
  readonly switchToSplit: typeof editModeActions.switchToSplit;
  readonly startMultiselectMode: typeof editModeActions.startMultiselectMode;
  readonly endMultiselectMode: typeof editModeActions.endMultiselectMode;
  readonly startBoxSelectMode: typeof editModeActions.startBoxSelectMode;
  readonly endBoxSelectMode: typeof editModeActions.endBoxSelectMode;
  readonly showMessage: (title: string, message: string | JSX.Element, onSubmit?: onSubmitEventType, onSubmitText?: string, isDissmisable?: boolean) => void;
  readonly fromInspectionMessage: typeof inspectionActions.fromInspectionMessage;
  readonly copy: typeof copyActions.copy;
  readonly paste: typeof copyActions.paste;
  readonly flipSelection: typeof copyActions.flip;
  readonly mirrorSelection: typeof copyActions.mirror;
  readonly checkConnection: typeof networkPersistenceActions.checkConnection;
  readonly toggleDrawingPreshapes: (drawPreshapes: boolean) => void;
  readonly centerOnLoad: () => void;
  readonly toggleToolbarLeft: () => void;
  readonly clearSketch: typeof sketchActions.clear;
  readonly clearHistory: typeof historyActions.clear;
  readonly resetCurrentSketch: typeof sketchPersistenceActions.resetCurrentSketch;
  readonly hideNumpadModal: () => void;
}

type Props = StateProps & ActionProps & RouteComponentProps;

interface State {
  userIsValid: boolean;
  sidebarLeft: number;
  sidebarTop: number;
  sketchHeight: number;
  isEmbedded: boolean;
  loadFirstSketch: boolean;
  userCompanyResponse: any
}

//
// Base UI Component; START HERE to follow the rendering process;
//

class MainScreen extends PureComponent<Props, State> {
  // eslint-disable-next-line react/sort-comp

  // CONTEXT: SketchData.
  // The purpose of the Sketchpad is to produce and handle the sketch data.
  //
  // (1) The data is loaded through src/ducks/persistance/SketchPersistance.ts actions.
  // - doLoadSketch attempts to get it from the backend, saving it to the localStorage value currentSketch.
  // - doGetLocalStorage takes the currentSketch json, and converts it into a Modal that it then starts the process of drawing.
  //
  // (2) The sketch elements are written to the state through a src/ducks/model/pages.ts functions:
  // - placePageObjects prepares all the elements to be rendered/drawn from the sketch Modal into the state
  //
  // (3) The sketch elements are drawn to the page from the state in the
  // MainScreen > SketchArea > Sketch > Page Component
  //
  // (Extra) Initial Loading: When sketch is opening up, it attempts to doLoadLastSketch.
  // - this is triggered when configuring the store, which initializes the redux sagas and calls the src/ducks/mount.ts doMount() action.
  // - this is simply how the functions in (1) end up being called on launch.

  private readonly focusRef: RefObject<HTMLDivElement>;

  private readonly toolBarRef: RefObject<HTMLDivElement>;

  private resizeObserver?: ResizeObserver;

  public constructor(props: Props) {
    super(props);
    this.focusRef = React.createRef<HTMLDivElement>();
    this.toolBarRef = React.createRef<HTMLDivElement>();

    const urlParameters = new URLSearchParams(window.location.search).toString();
    if (urlParameters) {
      sessionStorage.setItem('urlParams', urlParameters.toString());
    }

    this.state = {
      userIsValid: false,
      sidebarLeft: 0,
      sidebarTop: 0,
      sketchHeight: 0,
      isEmbedded: Boolean(new URLSearchParams(props.location.search).get('embedded')),
      loadFirstSketch: false,
      userCompanyList: []
    };
  }

  // eslint-disable-next-line react/sort-comp
  private setLocalStore = (params: URLSearchParams): void => {
    // domain?newSketch=true
    // we should clear all variables
    const newSketch = params.get('newSketch');
    if (newSketch) {
      localStorage.removeItem('sketchId');
      localStorage.removeItem('isCreated');
      localStorage.removeItem('isDirty');
    }

    // domain?sketchId=<id>
    // we should start application with existing sketch (load sketch)
    const sketchId = params.get('sketchId');
    if (sketchId) {
      localStorage.setItem('sketchId', sketchId);
      localStorage.setItem('isCreated', 'true');
      localStorage.setItem('isDirty', 'true');
    }
  };

  // eslint-disable-next-line react/sort-comp
  private grabFocus = (): void => {
    // we have to set focus to the svg editor
    // due to editor have to handle keyDown events
    // but not for modals (modal could have inputs and we don't want to lose focus on each change)
    const {
      isShowingAreaTypeModal,
      isShowingSubAreaModal,
      isShowingGlaBreakdownModal,
      isShowingPageGroupsModal,
      isShowingPrintPreviewModal,
      isShowingResizeSketchModal,
      isShowingSaveSketchModal,
      isShowingRescaleGridModal,
      isShowingLabelSidebar,
      isShowingSymbolSidebar,
      isPanning,
      hideSidebar
    } = this.props;
    if (
      !isShowingSaveSketchModal &&
      !isShowingRescaleGridModal &&
      !isShowingAreaTypeModal &&
      !isShowingSubAreaModal &&
      !isShowingGlaBreakdownModal &&
      !isShowingPageGroupsModal &&
      !isShowingPrintPreviewModal &&
      !isShowingResizeSketchModal &&
      !isShowingSymbolSidebar
    ) {
      // resolves issue with not being able to select labelItems by hiding during pan
      if (isPanning && isShowingLabelSidebar) hideSidebar();

      if (document.activeElement !== this.focusRef.current) {
        if (this.focusRef.current) {
          this.focusRef.current.focus();
        }
      }
    }
  };

  private handleMessage = (message: MessageEvent): void => {
    if (message.data && typeof message.data === 'string') {
      try {
        const { fromInspectionMessage } = this.props;
        if (message.data.trim().startsWith('{') || message.data.trim().startsWith('[')) {
          fromInspectionMessage(JSON.parse(message.data));
        } else {
          fromInspectionMessage({ customData: message.data });
        }
      } catch (err) {
        console.error('Error handling message:', err);
      }
    }
  };

  //
  // COMPONENT DID MOUNT; SKETCHPAD INITIAL COMPONENT; ENTRY POINT
  //
  public componentDidMount = async (): Promise<void> => {
    const { loadAreaTypes, checkConnection, centerOnLoad, hideNumpadModal } = this.props;

    if (!isMobileApplication()) {
      hideNumpadModal();
    }

    // Step 1: Initializing SketchPad
    window.history.pushState(null, ''); // locks app on one state
    window.addEventListener('popstate', this.handleBackAction); //  after first interaction with the app
    this.grabFocus();

    requestAnimationFrame(() => {
      this.setSidebarPos();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.setSidebarPos();
    });

    if (this.toolBarRef.current) {
      this.resizeObserver.observe(this.toolBarRef.current);
    }

    checkConnection(); // start network connection check

    window.addEventListener('message', this.handleMessage);

    window.onload = function onload() {
      centerOnLoad();
    };

    // Checking / Requiring Login
    const userIsValid = await getUserIsValid();
    const modesRequiringLogin = 'default;mobile;workbench';
    const LoginIsRequired = !userIsValid && modesRequiringLogin.includes(getSketchPadMode());
    if (LoginIsRequired) {
      await login(); // REDIRECTS to separate EagleID Login Site
    }

    loadAreaTypes();

    // handling Stored Parameters
    const storedParams = sessionStorage.getItem('urlParams');
    let loadFirstSketch = false;
    let userCompany = '';
    if (storedParams) {
      const { setOrderDetails, setSearchTerm, clearSketch, clearHistory, resetCurrentSketch } = this.props;
      const queryParams = new URLSearchParams(storedParams);

      // Set session Storage value for Sketch Mode
      // *This is how Auth.ts knows to recognize workbench mode
      const SketchMode = queryParams.get('SketchMode') || '';
      sessionStorage.setItem('sketchMode', SketchMode);

      // Set session Storage value for user Company
      // *This is how the Company Picker knows what Company is selected
      const companyId = queryParams.get('CompanyID') || '';
      if (companyId.length > 0) {
        sessionStorage.setItem('userCompany', companyId);
        userCompany = companyId;
      }

      const orderId = queryParams.get('OrderID') || '';
      const address = queryParams.get('address') || '';
      setOrderDetails({ companyId, orderId, address });

      // If an orderId is provided, attempt to load the latest sketch.
      const hasAssociatedOrder = orderId;
      if (hasAssociatedOrder) {
        // remove cached sketch data
        clearSketch();
        clearHistory();
        resetCurrentSketch();

        // start the search process, and set flag
        setSearchTerm(orderId);
        loadFirstSketch = true;
      }
    }

    // Requesting Company List
    const userCompanyListRequired = userIsValid && !userCompany;
    if (userCompanyListRequired) {
      getCompanies().then((body: any) => {
        let userCompanyList = body;
        if (body.body && typeof body.body === 'string') {
          userCompanyList = JSON.parse(body.body);
        }

        // If No companies are found, throw an error
        if (body.companies.length === 0) {
          this.companyListAuthErrorMessage();
          return;
        }

        this.setState((state: State) => ({ ...state, userCompanyList }));
      });
    }

    // Finalizing
    this.setState((state: State) => ({ ...state, userIsValid, loadFirstSketch }));
    centerOnLoad();
  };

  private updateNumpadWidth = (): void => {
    const { isShowingToolbarLeft, isShowingNumpadModal } = this.props;
    // If Numpad is not shown, skip updating its width
    if (!isShowingNumpadModal) {
      return;
    }
    let width = window.innerWidth;
    if (isShowingToolbarLeft && this.toolBarRef.current) {
      const toolbarWidth = this.toolBarRef.current.getBoundingClientRect().width;
      width -= width - toolbarWidth;
    }
    this.setState({ numpadWidth: width });
  };

  public componentDidUpdate = (prevProps: Props): void => {
    const {
      showMessage,
      userCompany,
      loadSketch,
      sketches,
      sketchesAreLoading,
      isShowingToolbarLeft,
      isShowingNumpadModal } = this.props;
    const { loadFirstSketch, userCompanyList } = this.state;

    this.grabFocus();
    this.setSidebarPos();

    // Handle Toolbar Adjustments
    const toolbarChanged = prevProps.isShowingToolbarLeft !== isShowingToolbarLeft;
    const numpadChanged = prevProps.isShowingNumpadModal !== isShowingNumpadModal;
    if (toolbarChanged || numpadChanged) {
      this.updateNumpadWidth();
    }

    // Company Picker
    const userMustSelectACompany = !userCompany && userCompanyList?.companies?.length > 0;
    if (userMustSelectACompany) {
      window.history.replaceState({}, '', window.location.href);
      showMessage(
        'Your Companies',
        <CompanyList body={userCompanyList} />,
        () => {
          // submit should write company to session storage, but if for any reason it is not there, then use the default..
          if (sessionStorage.getItem('selectedCompany')) {
            sessionStorage.setItem('userCompany', sessionStorage.getItem('selectedCompany') || '');
            sessionStorage.removeItem('selectedCompany');
          }
        },
        'Continue Login',
        false
      );
    }

    // IF we want to load the latest sketch from the order AND sketches have finished loading
    const doLoadFirstSketch = loadFirstSketch && !sketchesAreLoading;
    if (doLoadFirstSketch) {
      // If we have found sketches, select the latest and load it
      if (sketches.length > 0) {
        let latestSketch = sketches[0];
        for (let i = 0; i < sketches.length; i++) {
          if (sketches[i].last_modified_date > latestSketch.last_modified_date) latestSketch = sketches[i];
        }
        loadSketch(latestSketch.id);
      }

      // Whether we find sketches or not, close the request to load the sketch
      this.setState({ loadFirstSketch: false });
    }
  };

  public componentWillUnmount = (): void => {
    window.removeEventListener('popstate', this.handleBackAction);
    window.removeEventListener('message', this.handleMessage);
    if (this.resizeObserver && this.toolBarRef.current) {
      this.resizeObserver.unobserve(this.toolBarRef.current);
    }
  };

  // This function is only used as part of the Company Picker, which is bypassed when launched from Sketchpad
  // Therefore, it does not need the workbench exit handling.
  private logoutUser = async (): Promise<void> => {
    await logout({ beforeLogout });
  };

  private setSidebarPos = (): void => {
    requestAnimationFrame(() => {
      const toolBar = this.toolBarRef.current;
      const sketchDiv = document.getElementsByClassName('sketchDiv')[0] as HTMLElement;
      const width = toolBar?.getBoundingClientRect().width || 0;
      const top = sketchDiv?.getBoundingClientRect().top || 0;
      const height = sketchDiv?.getBoundingClientRect().height || 0;
      const { sidebarLeft, sidebarTop, sketchHeight } = this.state;
      if (
        sidebarLeft !== width ||
        sidebarTop !== top ||
        sketchHeight !== height
      ) {
        this.setState({
          sidebarLeft: width,
          sidebarTop: top,
          sketchHeight: height
        });
      }
    });
  };

  private handleBackAction = (): void => {
    const { showMessage } = this.props;
    showMessage(cannotGoBackDialog.title, cannotGoBackDialog.text);
    window.history.pushState(null, ''); // locks app on one state
  };

  private handleKeyDown = (keyEvent: React.KeyboardEvent): void => {
    // Prevent IE from navigating back on Backspace key press
    if (keyEvent.key === Keys.backspace) keyEvent.preventDefault();

    // prevent Mac's from crashing on CTRL-Left Press; TODO: Fix/Control this
    if (keyEvent.metaKey && keyEvent.key === 'ArrowLeft') keyEvent.preventDefault();

    //
    // metaKey is Cmd key on Mac
    // some keyboard's shortcuts on Mac use Cmd + key instead of Ctrl + key
    // warning: metaKey is working only for keydown event,
    // i.e. for keyUp and keyPress we should use other solution, more details here
    // https://stackoverflow.com/questions/3902635/how-does-one-capture-a-macs-command-key-via-javascript
    //

    const shortcutKey = this.normalizeKeyInput(keyEvent);
    const foundShortcut = this.shortcutActions.find((shortcut) =>
      (shortcut.keySequence === shortcutKey && (shortcut.isValidMode?.(keyEvent) ?? true)));

    if (!foundShortcut) return;
    foundShortcut.action(keyEvent);
  };

  private normalizeKeyInput = (event: React.KeyboardEvent): string => {
    /* Combine all key input into a single, normalized string */
    const sequence: string[] = [];
    if (event.ctrlKey) sequence.push('Ctrl');
    if (event.metaKey) sequence.push('Meta');
    if (event.altKey) sequence.push('Alt');
    if (event.shiftKey) sequence.push('Shift');

    const key = this.normalizeKey(event);
    if (!sequence.includes(key)) sequence.push(key);
    return sequence.join('+');
  }

  private normalizeKey = (event: React.KeyboardEvent): string => {
    /* Normalize each individual Key */
    const { key, keyCode } = event;
    if (Keys.arrowKeys.includes(key)) return 'AnyArrow';
    if (Keys.digitKeys.includes(key)) return 'AnyDigit';
    if (Keys.plusKeys.includes(key) || Keys.plusKeysCodes.includes(keyCode)) return 'Plus';
    if (Keys.minusKeys.includes(key) || Keys.minusKeysCodes.includes(keyCode)) return 'Minus';
    if (key === Keys.delete || key === Keys.backspace) return 'Delete';
    if (key === 'Control') return 'Ctrl';

    return key.length === 1
      ? key.toLowerCase()
      : key;
  }

  /* eslint-disable react/sort-comp */
  private shortcutActions: ShortcutDetails[] = [
    // JUMP TO ALIGNMENTS ACTIONS
    {
      name: 'Jump-Move To Alignment',
      keySequence: 'Ctrl+AnyArrow',
      description: 'Jump selected object(s) to Alignment Intersections',
      isValidMode: () => {
        const { isSelectedMode } = this.props;
        return isSelectedMode;
      },
      action: (event) => {
        const { jumpMoveByKeypad } = this.props;
        // GLITCHY. NEEDS FIX
        jumpMoveByKeypad(event.key);
      }
    },
    {
      name: '(MAC) Jump-Move To Alignment',
      keySequence: 'Alt+AnyArrow', // The Mac reserves Ctrl+ArrowLeft. We use the option key instead.
      description: '(MAC) Jump selected object(s) to Alignment Intersections.',
      isValidMode: () => {
        const { isSelectedMode } = this.props;
        return isSelectedMode;
      },
      action: (event) => {
        const { jumpMoveByKeypad } = this.props;
        // GLITCHY. NEEDS FIX
        jumpMoveByKeypad(event.key);
      }
    },
    {
      name: 'Draw-Move To Alignment',
      keySequence: 'Ctrl+AnyArrow',
      description: 'Move Prototype of Endpoint to Alignment Intersections',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return isDrawing;
      },
      action: (event) => {
        const { setNumpadLock, drawToDirectionAlignment } = this.props;
        if (!Keys.arrowKeys.includes(event.key)) return;
        const direction = event.key.toUpperCase().replace('ARROW', '');
        setNumpadLock(true);
        drawToDirectionAlignment(direction);
      }
    },
    {
      name: '(MAC) Draw-Move To Alignment',
      keySequence: 'Meta+AnyArrow', // The mac reserves Ctrl+Arrow Shortcuts. We use the Meta (Command) Key instead.
      description: '(MAC) Move Prototype of Endpoint to Alignment Intersections',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return isDrawing;
      },
      action: (event) => {
        const { setNumpadLock, drawToDirectionAlignment } = this.props;
        if (!Keys.arrowKeys.includes(event.key)) return;
        const direction = event.key.toUpperCase().replace('ARROW', '');
        setNumpadLock(true);
        drawToDirectionAlignment(direction);
      }
    },
    {
      name: 'Nudge-Move To Alignment',
      keySequence: 'Ctrl+AnyArrow',
      description: 'Move the Point to start Drawing From to Alignment Intersection.',
      isValidMode: () => {
        const { isEditMode } = this.props;
        return isEditMode;
      },
      action: (event) => {
        const { setNumpadLock, drawToDirectionAlignment } = this.props;
        if (!Keys.arrowKeys.includes(event.key)) return;
        const direction = event.key.toUpperCase().replace('ARROW', '');
        setNumpadLock(true);
        drawToDirectionAlignment(direction);
      }
    },
    {
      name: '(MAC) Nudge-Move To Alignment',
      keySequence: 'Meta+AnyArrow', // The mac reserves Ctrl+Arrow Shortcuts. We use the Meta (Command) Key instead.
      description: '(MAC) Move the Point to start Drawing From to Alignment Intersection.',
      isValidMode: () => {
        const { isEditMode } = this.props;
        return isEditMode;
      },
      action: (event) => {
        const { setNumpadLock, drawToDirectionAlignment } = this.props;
        if (!Keys.arrowKeys.includes(event.key)) return;
        const direction = event.key.toUpperCase().replace('ARROW', '');
        setNumpadLock(true);
        drawToDirectionAlignment(direction);
      }
    },
    {
      name: 'Move By Keypad Distance',
      keySequence: 'AnyDigit',
      description: 'Move the selected Object with Arrow Keys',
      isValidMode: () => {
        const { isSelectedMode } = this.props;
        return isSelectedMode;
      },
      action: (event) => {
        const { isShowingNumpadModal, showNumpadModalMin, addDrawInput } = this.props;
        if (!isShowingNumpadModal) showNumpadModalMin();
        addDrawInput(event.key);
      }
    },
    {
      name: 'Move By Keypad',
      keySequence: 'AnyArrow',
      description: 'Move the selected Object with Arrow Keys',
      isValidMode: () => {
        const { isSelectedMode } = this.props;
        return isSelectedMode;
      },
      action: (event) => {
        const { moveByKeypad, resetDrawInput } = this.props;
        moveByKeypad(event.key);
        resetDrawInput();
      }
    },
    {
      name: 'Prcise Move By Keypad',
      keySequence: 'Shift+AnyArrow',
      description: 'Move the selected Object with Arrow Keys',
      isValidMode: () => {
        const { isSelectedMode } = this.props;
        return isSelectedMode;
      },
      action: (event) => {
        const { preciseMoveByKeypad, resetDrawInput } = this.props;
        preciseMoveByKeypad(event.key);
        resetDrawInput();
      }
    },
    {
      name: 'Pan By Arrows',
      keySequence: 'AnyArrow', // The mac reserves Ctrl+Arrow Shortcuts. We use the Meta (Command) Key instead.
      description: 'Pan around the View with Arrow Keys',
      isValidMode: () => {
        const { isSelectedMode, isDrawing, isEditMode, isLockedToNumpad } = this.props;
        return !isSelectedMode && !isDrawing && !isEditMode && !isLockedToNumpad;
      },
      action: (event) => {
        const { startPan, viewCenterPoint, panViewBox, endPan } = this.props;
        startPan(viewCenterPoint);
        const delta = arrowDeltas[event.key];
        panViewBox({
          x: viewCenterPoint.x + delta.x,
          y: viewCenterPoint.y + delta.y
        });
        endPan();
      }
    },
    {
      name: 'Jump to Nearest Point',
      keySequence: 'j',
      description: 'Jump the Anchor to the nearest point',
      isValidMode: () => {
        const { isEditMode } = this.props;
        return isEditMode;
      },
      action: () => {
        const { setNumpadLock, jumpToCorner } = this.props;
        setNumpadLock(true);
        jumpToCorner();
      }
    },
    // COPY / PASTE ACTIONS
    {
      name: 'Copy',
      keySequence: 'Ctrl+c',
      description: 'Copy Selected Object',
      action: () => {
        const { selectedObjects, copy } = this.props;
        if (selectedObjects.size > 0) copy();
      }
    },
    {
      name: 'Paste',
      keySequence: 'Ctrl+v',
      description: 'Paste Copied Object',
      action: () => {
        const { paste } = this.props;
        paste();
      }
    },
    {
      name: 'Copy and Paste',
      keySequence: 'Ctrl+d',
      description: 'Copy and Immediately Paste Copied Object', // Ctrl-D has a interfering Mac shortCut
      action: () => {
        const { copy, paste } = this.props;
        copy();
        paste();
      }
    },
    // FIGURE ACTIONS
    {
      name: 'Mirror',
      keySequence: 'Ctrl+g',
      description: 'Mirror selected object(s)',
      action: () => {
        const { selectedObjects, mirrorSelection } = this.props;
        if (!selectedObjects.size) return;
        mirrorSelection();
      }
    },
    {
      name: 'Flip',
      keySequence: 'Ctrl+f',
      description: 'Flip selected object(s)',
      action: () => {
        const { selectedObjects, flipSelection } = this.props;
        if (!selectedObjects.size) return;
        flipSelection();
      }
    },
    // SKETCH VIEW ACTIONS
    {
      name: 'Reset View',
      keySequence: 'Ctrl+AnyDigit',
      description: 'Reset View Box',
      action: (event) => {
        const { resetViewBox } = this.props;
        if (!Keys.zero.includes(event.key)) return;
        resetViewBox();
      }
    },
    {
      name: 'Zoom In',
      keySequence: 'Ctrl+Plus',
      description: 'Zoom in to the View Box',
      action: (event) => {
        const { zoomViewBox, viewCenterPoint } = this.props;
        event.preventDefault();
        zoomViewBox({ location: viewCenterPoint, increments: -1 });
      }
    },
    {
      name: 'Zoom Out',
      keySequence: 'Ctrl+Minus',
      description: 'Zoom out of the View Box',
      action: (event) => {
        const { zoomViewBox, viewCenterPoint } = this.props;
        event.preventDefault();
        zoomViewBox({ location: viewCenterPoint, increments: 1 });
      }
    },
    {
      name: 'Center',
      keySequence: 'c',
      description: 'Center the Sketch on currently drawn Objects',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return !isDrawing;
      },
      action: () => {
        const { centerAll } = this.props;
        centerAll();
      }
    },
    // SKETCH DATA ACTIONS
    {
      name: 'Save Sketch',
      keySequence: 'Ctrl+s',
      description: 'Save Sketch',
      action: (event) => {
        const { currentPage, svgElement } = this.props;
        event.preventDefault();

        if (!currentPage) return;
        const formattedSketchObjects = formatSketchObjects(currentPage.objects);
        if (formattedSketchObjects.points.size && svgElement) {
          const pngUriPromise = svgAsPngUri(svgElement, printConfig, formattedSketchObjects);
          pngUriPromise.then((sketchPngUri) => {
            const downloadLink = document.createElement('a');
            downloadLink.href = sketchPngUri;
            downloadLink.download = 'sketch-save.png';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
          });
        }
      }
    },
    // DRAWING ACTION SHORTCUTS
    // Some of these are not currently Modelocked; We should consider locking them to their valid modes.
    {
      name: 'Undo',
      keySequence: 'Ctrl+z',
      description: 'Undo',
      action: () => {
        const { canUndo, undo } = this.props;
        if (canUndo) undo();
      }
    },
    {
      name: 'Redo',
      keySequence: 'Ctrl+y',
      description: 'Redo',
      action: () => {
        const { canRedo, redo } = this.props;
        if (canRedo) redo();
      }
    },
    {
      name: 'AutoFinish',
      keySequence: 'a',
      description: 'Complete the Current Figure',
      action: () => {
        const { autoFinishSegment } = this.props;
        autoFinishSegment();
      }
    },
    {
      name: 'Start Draw With Enter',
      keySequence: 'Enter',
      description: 'Complete the Current Figure',
      isValidMode: () => {
        const { isEditMode } = this.props;
        return isEditMode;
      },
      action: () => {
        const { startDraw, startLine, getCurserPoint } = this.props;
        startDraw();
        startLine(getCurserPoint);
      }
    },
    {
      name: 'Add Numpad Digit',
      keySequence: 'AnyDigit',
      description: 'Update Numpad Draw Input with Digits',
      isValidMode: () => {
        const { isDrawing, isEditMode } = this.props;
        return isDrawing || isEditMode;
      },
      action: (event) => {
        const { isShowingNumpadModal, showNumpadModalMin, addDrawInput } = this.props;
        if (!isShowingNumpadModal) showNumpadModalMin();
        addDrawInput(event.key);
      }
    },
    {
      name: 'Add Numpad Direction',
      keySequence: 'AnyArrow',
      description: 'Update Numpad Draw Input with Directions',
      isValidMode: () => {
        const { isDrawing, isEditMode } = this.props;
        return isDrawing || isEditMode;
      },
      action: (event) => {
        const { isShowingNumpadModal, showNumpadModalMin, addDrawInput, drawInputSequence } = this.props;
        if (!isShowingNumpadModal) showNumpadModalMin();
        // If numbers were not the last input, provide 1 (1 gridsquare).
        if (drawInputSequence.length < 1 || !drawInputSequence[drawInputSequence.length - 1]) {
          addDrawInput('1');
        }
        // if no digits, or last movement was arrow key, add digits
        addDrawInput(event.key);
      }
    },
    {
      name: 'Add Precise Numpad Direction',
      keySequence: 'Shift+AnyArrow',
      description: 'Update Numpad Draw Input with Directions',
      isValidMode: () => {
        const { isDrawing, isEditMode } = this.props;
        return isDrawing || isEditMode;
      },
      action: (event) => {
        const { isShowingNumpadModal, showNumpadModalMin, addDrawInput, drawInputSequence, unitOfMeasure } = this.props;
        if (!isShowingNumpadModal) showNumpadModalMin();
        // If numbers were not the last input, provide .1 (1/10 gridsquare).
        if (drawInputSequence.length < 1 || !drawInputSequence[drawInputSequence.length - 1]) {
          addDrawInput('.');
          // if feet/inches, move 1/12.
          if (unitOfMeasure === 'feet') {
            addDrawInput('0');
            addDrawInput('8');
            addDrawInput('3');
          } else {
            // for other preceisions, move 1/10th.
            addDrawInput('1');
          }
        }
        addDrawInput(event.key);
      }
    },
    {
      name: 'Finish Numpad Draw Input',
      keySequence: 'Enter',
      description: 'Finish Numpad Draw Input; Draws a line from the current point to the new point.',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return isDrawing;
      },
      action: (event) => {
        const {
          isAngleInputEnabled,
          currentDirection,
          finishDrawInput,
          currentValue,
          unitOfMeasure,
          currentGridSize,
          previousStartPoint,
          endPoint,
          currentAngleValue,
          setEndPoint,
          centerAll,
          resetDrawInput
        } = this.props;

        event.preventDefault();
        // Handle Angle Calculation
        if (isAngleInputEnabled && currentDirection) {
          const { feet, inches } = parseString(currentValue);
          const pixels = unitOfMeasure === 'meters' ?
            metersToPixels(parseFloat(currentValue), currentGridSize) :
            feetToPixels(feet, inches);
          const newCoordinates = coordinatesByAngle(previousStartPoint, endPoint, pixels, +currentAngleValue, currentDirection);
          setEndPoint({ ...newCoordinates }, true);
          centerAll();
          resetDrawInput();
        } else {
          // Default draw straight wall.
          finishDrawInput();
        }
      }
    },
    {
      name: 'Cancel Draw',
      keySequence: 't',
      description: 'Cancel Draw and return to Selection',
      action: () => {
        const { switchToSelection } = this.props;
        this.cancelDraw();
        switchToSelection();
      }
    },
    {
      name: 'Delete',
      keySequence: 'Delete',
      description: 'Delete',
      action: () => {
        const {
          selectedObjects,
          removeSelectedObjects,
          isShowingNumpadModal,
          currentValue,
          removeLastInput,
          drawInputSequence,
          resetDrawInput,
          setEndPoint,
          isDrawing,
          undoSegment,
          previousStartPoint
        } = this.props;

        if (selectedObjects.size) removeSelectedObjects();
        if (isShowingNumpadModal) {
          if (currentValue.length > 0) {
            // Case 1: If we have input in the numpad Text Field
            removeLastInput();
          } else if (drawInputSequence.length > 0) {
            // Case 2: If we have a prototype segment
            resetDrawInput();
            setEndPoint(previousStartPoint, true);
          } else if (isDrawing) {
            // Case 3: Default undo last drawn segment.
            undoSegment();
          }
        }
      }
    },
    {
      name: 'Curve',
      keySequence: 'u',
      description: 'Add Curve',
      action: () => {
        const { canCreateCurve, addCurve } = this.props;
        if (canCreateCurve) addCurve();
      }
    },
    {
      name: 'Handle Left Angle',
      keySequence: 'l',
      description: 'Start a Left Angle Sequence',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return isDrawing;
      },
      action: () => {
        const { walls, currentValue, setCurrentDirection, isAngleInputEnabled, setAngleInput } = this.props;
        if (!walls.size) {
          toast.warning(messages.angleTriggerWithoutWalls);
          return;
        }
        if (!+currentValue) {
          toast.warning(messages.angleTriggerWithoutDistance);
          return;
        }
        setCurrentDirection('ArrowLeft');
        if (currentValue !== '' && walls.size && !isAngleInputEnabled) setAngleInput();
      }
    },
    {
      name: 'Handle Right Angle',
      keySequence: 'r',
      description: 'Start a Right Angle Sequence',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return isDrawing;
      },
      action: () => {
        const { walls, currentValue, setCurrentDirection, isAngleInputEnabled, setAngleInput } = this.props;
        if (!walls.size) {
          toast.warning(messages.angleTriggerWithoutWalls);
          return;
        }
        if (!+currentValue) {
          toast.warning(messages.angleTriggerWithoutDistance);
          return;
        }
        setCurrentDirection('ArrowRight');
        if (currentValue !== '' && walls.size && !isAngleInputEnabled) setAngleInput();
      }
    },
    {
      name: 'Split',
      keySequence: 'p',
      description: 'Switch to Split',
      action: () => {
        const { switchToSplit } = this.props;
        switchToSplit();
      }
    },
    // TOOLBAR LEFT Shortcuts
    {
      name: 'Prepare To Draw Exterior Walls',
      keySequence: 'e',
      description: 'Prepare To Draw Exterior Walls',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return !isDrawing;
      },
      action: () => {
        const { toggleDrawingInteriorWalls, toggleDrawingPreshapes, switchToEdit } = this.props;
        toggleDrawingInteriorWalls(false);
        toggleDrawingPreshapes(false);
        switchToEdit(false);
      }
    },
    {
      name: 'Prepare To Draw Interior Walls',
      keySequence: 'i',
      description: 'Prepare To Draw Interior Walls',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return !isDrawing;
      },
      action: () => {
        const { toggleDrawingInteriorWalls, toggleDrawingPreshapes, switchToEdit } = this.props;
        toggleDrawingInteriorWalls(true);
        toggleDrawingPreshapes(false);
        switchToEdit(false);
      }
    },
    {
      name: 'Start MultiSelect Mode',
      keySequence: 'Shift',
      description: 'Start Multi Select Mode',
      action: () => {
        const { endBoxSelectMode, startMultiselectMode, toggleDrawingStraightWalls } = this.props;
        endBoxSelectMode();
        startMultiselectMode();
        toggleDrawingStraightWalls(true);
      }
    },
    {
      name: 'Start Box Mode',
      keySequence: 'Alt',
      description: 'Start Multi Select Mode',
      action: () => {
        const { endMultiselectMode, startBoxSelectMode } = this.props;
        endMultiselectMode();
        startBoxSelectMode();
      }
    },
    // SIDEBAR ACTIONS
    {
      name: 'Toggle Label Sidebar',
      keySequence: 'l',
      description: 'Toggle Label Sidebar',
      isValidMode: () => {
        const { isDrawing } = this.props;
        return !isDrawing;
      },
      action: () => {
        const { isShowingLabelSidebar, showLabelSidebar, hideSidebar } = this.props;
        if (!isShowingLabelSidebar) {
          showLabelSidebar();
        } else {
          hideSidebar();
        }
      }
    },
    {
      name: 'Toggle Symbol Sidebar',
      keySequence: 's',
      description: 'Toggle Symbol Sidebar',
      action: () => {
        const { isShowingSymbolSidebar, showSymbolSidebar, hideSidebar } = this.props;
        if (!isShowingSymbolSidebar) {
          showSymbolSidebar();
        } else {
          hideSidebar();
        }
      }
    },
    {
      name: 'Toggle Shapes Sidebar',
      keySequence: 'h',
      description: 'Toggle Shapes/Polygon Sidebar',
      action: () => {
        const { isShowingPolygonSidebar, showPolygonSidebar, hidePolygonSidebar } = this.props;
        if (!isShowingPolygonSidebar) {
          showPolygonSidebar();
        } else {
          hidePolygonSidebar();
        }
      }
    },
    // EDITING ACTIONS
    {
      name: 'Cancel',
      keySequence: 'Escape',
      description: 'Cancel current Action',
      action: () => {
        const { isInStampMode, toggleStampMode, endBoxSelectMode } = this.props;
        this.cancelDraw();
        if (isInStampMode) toggleStampMode();
        endBoxSelectMode();
      }
    },
  ];

  private handleKeyUp = (keyEvent: React.KeyboardEvent): void => {
    // TODO: triggers even when modals are op=
    const { endMultiselectMode, endBoxSelectMode, toggleDrawingStraightWalls } = this.props;
    if (keyEvent.key === Keys.shift) {
      endMultiselectMode();
      toggleDrawingStraightWalls(false);
    } else if (keyEvent.key === Keys.alt) {
      endBoxSelectMode();
    }
  };

  private cancelDraw = (): void => {
    const { isDrawing, switchToEdit, isEditMode, endDraw, setNumpadLock } = this.props;

    // EditMode is before the first point is placed.
    // DrawingMode is while placing points.
    if (isEditMode) {
      endDraw();
    }

    if (isDrawing) {
      setNumpadLock(false);
      switchToEdit();
    }
  };

  // STP SSS-420 This will replace the bottom tab in the mobile application
  private inspectionTouchStart = (): void => {
    window.SendSketchToMobile();
  };

  private companyListAuthErrorMessage(): void {
    const { showMessage } = this.props;
    showMessage(
      'Auth Error',
      <div>
        There was an Authorization Error with your profile.
        <br />
        Please retry login or contact ACI Support.
        <br />
        <br />
        ACI Support: 800.234.8727
      </div>,
      () => {
        this.logoutUser();
      },
      'Retry Login',
      false
    );
  }

  // React Context: Render the UI, will be processed every time anything is changed.
  public render = (): JSX.Element => {
    const {
      location: { search },
      toggleToolbarLeft,
      isShowingAreaTypeModal,
      isShowingSubAreaModal,
      isShowingGlaBreakdownModal,
      isShowingPageGroupsModal,
      isShowingPrintPreviewModal,
      isShowingResizeSketchModal,
      isShowingSaveSketchModal,
      isShowingRescaleGridModal,
      isShowingKnownLengthPromptModal,
      isShowingSettingsModal,
      isShowingSketchSearchModal,
      isShowingToolbarLeft,
      isShowingLabelSidebar,
      isShowingSymbolSidebar,
      isShowingPolygonSidebar,
      isShowingNumpadModal,
      isApplyingModel,
      isShowingUnsavedModal,
    } = this.props;

    const { userIsValid, sidebarLeft, sidebarTop, sketchHeight, isEmbedded } = this.state;
    this.setLocalStore(new URLSearchParams(search));

    const modesNotRequiringLogin = 'desktop;';
    return userIsValid || modesNotRequiringLogin.includes(getSketchPadMode()) ? (
      <ThemeProvider theme={AciCoreTheme}>
        <DndProvider backend={HTML5Backend}>
          {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
          <div css={styles} ref={this.focusRef} tabIndex={0} role="presentation" onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp}>

            {/* Header Banner Bar */}
            {!isEmbedded && <ToolbarTop />}

            {/* SketchPad */}
            <Main>
              <ToolbarLeft innerRef={this.toolBarRef} pose={getDisplayPose(isShowingToolbarLeft)} />
              {!isEmbedded && !isShowingSymbolSidebar && !isShowingLabelSidebar && !isShowingPolygonSidebar && (
                <Button
                  css={{
                    position: 'absolute',
                    top: 23,
                    left: isShowingToolbarLeft ? sidebarLeft + 10 : 10,
                    zIndex: 1,
                    cursor: 'pointer',
                    height: 10,
                    background: 'white',
                    borderRadius: 5,
                    minWidth: '34px !important',
                    border: 0,
                    padding: 14,
                    boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                    display: isShowingSymbolSidebar ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'left 0.3s ease-in-out',
                    boxSizing: 'border-box',
                  }}
                  onClick={toggleToolbarLeft}
                >
                  {isShowingToolbarLeft ? <ChevronLeft /> : <ChevronRight />}
                </Button>
              )}

              <LabelSidebar left={sidebarLeft} pose={getDisplayPose(isShowingLabelSidebar)} />
              <SymbolSidebar left={sidebarLeft} pose={getDisplayPose(isShowingSymbolSidebar)} />
              <PolygonSidebar left={sidebarLeft} pose={getDisplayPose(isShowingPolygonSidebar)} />
              <SketchArea />
              <AppVersion />
            </Main>

            {/* Modal and Comment Components */}
            {!isApplyingModel && <PrintTableView />}
            {isShowingAreaTypeModal && <AreaTypeModal />}
            {isShowingSubAreaModal && <SubAreaModal />}
            <PageGroupsModal top={sidebarTop} pose={getDisplayPose(isShowingPageGroupsModal)} />
            {!isApplyingModel && <GlaBreakdownModal top={sidebarTop} pose={getDisplayPose(isShowingGlaBreakdownModal)} />}
            <NumpadMenuModal top={sidebarTop} left={isShowingToolbarLeft ? sidebarLeft : 0} height={sketchHeight} pose={getDisplayPose(isShowingNumpadModal)} />
            <SettingsModal top={sidebarTop} pose={getDisplayPose(isShowingSettingsModal)} />
            <SketchSearchModal top={sidebarTop} pose={getDisplayPose(isShowingSketchSearchModal)} />
            {isShowingKnownLengthPromptModal && <KnownLengthPromptModal />}
            {isShowingSaveSketchModal && <SaveSketchModal />}
            {isShowingResizeSketchModal && <ResizeSketchModal />}
            {isShowingRescaleGridModal && <RescaleGridModal />}
            {isShowingUnsavedModal && <UnsavedModal />}
            {!isIE11 && <PrintPreviewModal pose={getDisplayPose(isShowingPrintPreviewModal)} />}
            <ToastContainer />
            <MessageContainer />
          </div>
          <Preview />
          {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
        </DndProvider>

        {/* If launched from our Mobile App, add a matching footer banner bar that can bring us back to it. */}
        {
          isMobileApplication() && (
            <div role="presentation" css={mobileTabDivStyle}>
              <div css={mobileTabBtnDivStyle}>
                <MobileSketchIcon css={mobileIconStyle} />
                <span css={mobileTabSpanStyle}>Sketch</span>
              </div>
              <div css={mobileTabBtnDivStyle} onTouchStart={this.inspectionTouchStart}>
                <MobileOrdersIcon css={mobileIconStyle} />
                <span css={mobileTabSpanStyle}>Inspection</span>
              </div>
            </div>
          )
        }
      </ThemeProvider>
    ) : (
      <div>Redirecting to Login...</div>
    );
  };
}

export default React.memo(
  connect(
    (state: RootState): StateProps => ({
      unitOfMeasure: settingsSelectors.getUnitOfMeasure(state),
      currentGridSize: settingsSelectors.getGridSizeInFeet(state),
      startPoint: drawsSelectors.getStartPoint(state),
      endPoint: drawsSelectors.getEndPoint(state),
      previousStartPoint: drawsSelectors.getPreviousStartPoint(state),
      viewCenterPoint: viewportSelectors.getViewCenterPoint(state),
      isDrawing: editModeSelectors.isDrawingMode(state),
      isEditMode: editModeSelectors.isEditMode(state),
      isPanning: viewportSelectors.isPanning(state),
      isSelectedMode: editModeSelectors.isSelectedMode(state),
      isShowingNumpadModal: numpadModalSelectors.isShowing(state),
      isShowingAreaTypeModal: areaTypeModalSelectors.isShowing(state),
      isShowingSubAreaModal: subAreaModalSelectors.isShowing(state),
      isShowingGlaBreakdownModal: glaBreakdownModalSelectors.isShowing(state),
      isShowingPageGroupsModal: pageGroupsModalSelectors.isShowing(state),
      isShowingPrintPreviewModal: printPreviewModalSelectors.isShowing(state),
      isShowingSaveSketchModal: saveSketchModalSelectors.isShowing(state),
      isShowingResizeSketchModal: resizeSketchModalSelectors.isResizeShowing(state),
      isShowingRescaleGridModal: resizeSketchModalSelectors.isRescaleShowing(state),
      isShowingKnownLengthPromptModal: resizeSketchModalSelectors.isKnownLengthPromptShowing(state),
      isShowingSketchSearchModal: sketchSearchModalSelectors.isShowing(state),
      isShowingSettingsModal: settingsModalSelectors.isShowing(state),
      isShowingToolbarLeft: sidebarSelectors.isShowingToolbarLeft(state),
      isShowingLabelSidebar: sidebarSelectors.isShowingLabels(state),
      isShowingSymbolSidebar: sidebarSelectors.isShowingSymbols(state),
      isShowingPolygonSidebar: sidebarSelectors.isShowingPolygons(state),
      isInStampMode: sidebarSelectors.isInStampMode(state),
      canCreateCurve: selectionSelectors.isLineWallSelected(state),
      canUndo: historySelectors.canUndo(state),
      canRedo: historySelectors.canRedo(state),
      selectedObjects: editModeSelectors.getSelectedObjects(state),
      currentPage: pagesSelectors.getCurrentPage(state),
      svgElement: viewportSelectors.getSvgElement(state),
      svgSize: viewportSelectors.getSvgSize(state),
      drawInputSequence: drawWithKeypadSelectors.getDrawInputSequence(state),
      currentDirection: drawWithKeypadSelectors.getCurrentDirection(state),
      currentValue: drawWithKeypadSelectors.getCurrentValue(state),
      currentAngleValue: drawWithKeypadSelectors.getCurrentAngleValue(state),
      isAngleInputEnabled: drawWithKeypadSelectors.getIsAngleInputEnabled(state),
      walls: wallSelectors.getAllWalls(state),
      isApplyingModel: sketchPersistenceSelectors.isApplyingModel(state),
      userCompany: sessionStorage.getItem('userCompany') || '',
      orderId: orderDetailsSelectors.getOrderId(state),
      sketches: sketchesPersistenceSelectors.getSketches(state),
      sketchesAreLoading: sketchesPersistenceSelectors.isLoading(state),
      isShowingUnsavedModal: unsavedModalSelectors.isShowing(state),
      getCurserPoint: drawsSelectors.getCurserPoint(state),
      isLockedToNumpad: drawWithKeypadSelectors.getNumpadLocked(state),
    }),
    {
      loadAreaTypes: areaTypePersistenceActions.loadAreaTypes,
      toggleDrawingStraightWalls: drawActions.toggleDrawingStraightWalls,
      showNumpadModal: numpadModalActions.show,
      showNumpadModalMin: numpadModalActions.showMin,
      endDraw: editModeActions.switchToSelection,
      switchToSelection: editModeActions.switchToSelection,
      switchToEdit: editModeActions.switchToEdit,
      switchToSplit: editModeActions.switchToSplit,
      startMultiselectMode: editModeActions.startMultiselectMode,
      startBoxSelectMode: editModeActions.startBoxSelectMode,
      endMultiselectMode: editModeActions.endMultiselectMode,
      endBoxSelectMode: editModeActions.endBoxSelectMode,
      addCurve: drawCurveActions.addCurve,
      zoomViewBox: viewportActions.zoomViewBoxByIncrement,
      startPan: viewportActions.startPan,
      panViewBox: viewportActions.panViewBox,
      endPan: viewportActions.endPan,
      resetViewBox: viewportActions.resetViewBox,
      centerAll: viewportActions.centerAll,
      centerOnLoad: viewportActions.centerOnLoad,
      removeLastInput: drawWithKeypadActions.removeLastInput,
      setCurrentDirection: drawWithKeypadActions.setCurrentDirection,
      setCurrentAngleValue: drawWithKeypadActions.setCurrentAngleValue,
      setAngleInput: drawWithKeypadActions.setAngleInput,
      addDrawInput: drawWithKeypadActions.addDrawInput,
      finishDrawInput: drawWithKeypadActions.finishDrawInput,
      resetDrawInput: drawWithKeypadActions.resetDrawInput,
      resetCurrentValue: drawWithKeypadActions.resetCurrentValue,
      moveByKeypad: moveObjectsActions.moveByKeypad,
      preciseMoveByKeypad: moveObjectsActions.preciseMoveByKeypad,
      jumpMoveByKeypad: moveObjectsActions.jumpMoveByKeypad,
      undo: historyActions.undo,
      redo: historyActions.redo,
      removeSelectedObjects: removeObjects.removeSelectedObjects,
      showLabelSidebar: sidebarActions.showLabels,
      showPolygonSidebar: sidebarActions.showPolygons,
      hidePolygonSidebar: sidebarActions.hide,
      showSymbolSidebar: sidebarActions.showSymbols,
      hideSidebar: sidebarActions.hide,
      toggleStampMode: sidebarActions.toggleStampMode,
      autoFinishSegment: drawActions.autoFinishSegment,
      toggleDrawingInteriorWalls: drawActions.toggleDrawingInteriorWalls,
      showMessage: messageContainerActions.show,
      fromInspectionMessage: inspectionActions.fromInspectionMessage,
      copy: copyActions.copy,
      paste: copyActions.paste,
      flipSelection: copyActions.flip,
      mirrorSelection: copyActions.mirror,
      setEndPoint: drawActions.setEndPointWithoutSnapping,
      finishSegment: drawActions.finishSegment,
      undoSegment: drawActions.undoSegment,
      checkConnection: networkPersistenceActions.checkConnection,
      toggleDrawingPreshapes: drawActions.toggleDrawingPreshapes,
      setOrderDetails: orderActions.setOrderDetails,
      toggleToolbarLeft: sidebarActions.toggleToolbarLeft,
      setSearchTerm: sketchesPersistenceActions.setSearchTerm,
      loadSketch: sketchPersistenceActions.loadSketch,
      clearSketch: sketchActions.clear,
      clearHistory: historyActions.clear,
      resetCurrentSketch: sketchPersistenceActions.resetCurrentSketch,
      drawToDirectionAlignment: drawActions.drawToDirectionAlignment,
      hideNumpadModal: numpadModalActions.hide,
      startDraw: editModeActions.switchToDrawing,
      startLine: drawActions.startDraw,
      setNumpadLock: drawWithKeypadActions.setNumpadLock,
      jumpToCorner: drawActions.jumpToCorner
    },
  )(withRouter(MainScreen)),
);
